/**
 * 直付通科技独立收银台 —— 与模板 zhifutdPay.html 对齐。
 * 二维码和手机「立即支付」都打开 /pay/submitwap/，支付宝内直接半屏，不进萌芙确认页。
 */
import { executeZhifutdPay, fetchZhifutdCashierPage } from '@/api/api'
import { buildQrImageSrc, computeQrPixelSize } from '@/utils/qrRender'

/** 只认 UA。alipayjsapi 会在 Chrome 里挂 window.ap，不能用来判断是否在支付宝内。 */
export function isAlipayWebView () {
  if (typeof navigator === 'undefined') return false
  const ua = (navigator.userAgent || '').toLowerCase()
  return ua.indexOf('alipayclient') >= 0 || ua.indexOf('aliapp') >= 0
    || ua.indexOf('nebula') >= 0 || ua.indexOf('alipay') >= 0
}

export function isDesktopBrowser () {
  if (typeof navigator === 'undefined') return false
  if (isAlipayWebView()) return false
  const ua = (navigator.userAgent || '').toLowerCase()
  return !/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i.test(ua)
}

export function isExecutePayloadReady (d) {
  if (!d || d.expired) return false
  return !!(String(d.payUrl || '').trim() || String(d.codeUrl || '').trim() || String(d.channelPayUrl || '').trim())
}

export async function loadPageContext (orderId, token) {
  const ctx = await fetchZhifutdCashierPage(orderId, token)
  if (!ctx) {
    throw new Error('页面加载失败')
  }
  return ctx
}

export async function requestExecute (orderId, token) {
  try {
    const d = await executeZhifutdPay(orderId, token)
    if (!d) throw new Error('网络异常')
    return d
  } catch (err) {
    if (err && err.data) {
      const vo = err.data
      if (vo.expired) {
        const expiredErr = new Error(vo.message || '订单已过期')
        expiredErr.expiredPayload = vo
        throw expiredErr
      }
      throw new Error(vo.message || err.message || '出链失败')
    }
    throw new Error((err && err.message) || '出链失败')
  }
}

export function formatCountdown (leftMs) {
  const s = Math.max(0, Math.floor(leftMs / 1000))
  const h = String(Math.floor(s / 3600)).padStart(2, '0')
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
  const sec = String(s % 60).padStart(2, '0')
  return h + ':' + m + ':' + sec
}

export async function renderQrToElement (el, text) {
  if (!el || !text) return false
  const size = computeQrPixelSize(text)
  const src = await buildQrImageSrc(text, { pixelSize: size })
  if (!src) return false
  el.innerHTML = ''
  const img = document.createElement('img')
  img.src = src
  img.width = size
  img.height = size
  img.alt = '支付二维码'
  el.appendChild(img)
  return true
}

function pickLaunchUrl (payload) {
  return String((payload && (payload.payUrl || payload.channelPayUrl || payload.codeUrl)) || '').trim()
}

function isHttpPayUrl (raw) {
  return /^https?:\/\//i.test(String(raw || '').trim())
}

/** 展示页 /pay/qrcode/、/pay/submit/ 换成测试链接同款 /pay/submitwap/。 */
function toDirectPayUrl (raw) {
  let u = String(raw || '')
  u = u.replace(/\/pay\/qrcode\/([^/?#]+)\/?/i, '/pay/submitwap/$1/')
  u = u.replace(/\/pay\/submit\/([^/?#]+)\/?/i, '/pay/submitwap/$1/')
  return u
}

function directPayUrl (payload) {
  const codeUrl = String((payload && payload.codeUrl) || '').trim()
  if (/qr\.alipay\.com/i.test(codeUrl) || /^alipayqr:\/\//i.test(codeUrl)) return codeUrl
  if (isHttpPayUrl(codeUrl)) return toDirectPayUrl(codeUrl)
  const pay = pickLaunchUrl(payload)
  if (isHttpPayUrl(pay)) return toDirectPayUrl(pay)
  return ''
}

/** 二维码内容与测试页一致：submitwap，或上游直接给的 qr.alipay.com。 */
export function pickQrText (payload) {
  return directPayUrl(payload)
}

function assignAlipayScheme (scheme) {
  const u = String(scheme || '').trim()
  if (!u) return
  try {
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.src = u
    document.body.appendChild(iframe)
  } catch (e) { /* ignore */ }
  setTimeout(() => { window.location.href = u }, 120)
}

function openDirectPay (payload) {
  const httpUrl = directPayUrl(payload)
  if (!isHttpPayUrl(httpUrl)) {
    throw new Error('支付链接缺失')
  }
  if (isAlipayWebView()) {
    window.location.href = httpUrl
    return
  }
  assignAlipayScheme('alipays://platformapi/startapp?appId=20000067&url=' + encodeURIComponent(httpUrl))
}

/** 打开 submitwap。支付宝内直接进入半屏；支付宝外用 20000067 拉起，与测试页按钮一致。 */
export async function tryZhifutdHalfScreen (payload) {
  if (!isExecutePayloadReady(payload)) return false
  openDirectPay(payload)
  return true
}
